/**
 * Strapi v5 Filter Query Builder
 * 
 * Type-safe builder for complex filters with consistent operator handling.
 * Encodes values properly and prevents escaping issues.
 * 
 * Usage:
 * ```
 * filter()
 *   .equals('status', 'active')
 *   .in('id', [1, 2, 3])
 *   .notNull('deletedAt')
 *   .contains('name', 'parker')
 *   .build()
 * ```
 */

type FilterOperator = '$eq' | '$ne' | '$lt' | '$lte' | '$gt' | '$gte' | '$in' | '$notIn' | '$contains' | '$notNull' | '$null';

interface FilterCondition {
    field: string;
    operator: FilterOperator;
    value?: unknown;
}

/**
 * Builder for Strapi v5 filter queries
 */
export class StrapiFilterBuilder {
    private conditions: FilterCondition[] = [];

    /**
     * Equals condition
     */
    equals(field: string, value: unknown): this {
        this.conditions.push({ field, operator: '$eq', value });
        return this;
    }

    /**
     * Not equals condition
     */
    notEquals(field: string, value: unknown): this {
        this.conditions.push({ field, operator: '$ne', value });
        return this;
    }

    /**
     * Less than condition
     */
    lessThan(field: string, value: unknown): this {
        this.conditions.push({ field, operator: '$lt', value });
        return this;
    }

    /**
     * Less than or equal condition
     */
    lessThanOrEqual(field: string, value: unknown): this {
        this.conditions.push({ field, operator: '$lte', value });
        return this;
    }

    /**
     * Greater than condition
     */
    greaterThan(field: string, value: unknown): this {
        this.conditions.push({ field, operator: '$gt', value });
        return this;
    }

    /**
     * Greater than or equal condition
     */
    greaterThanOrEqual(field: string, value: unknown): this {
        this.conditions.push({ field, operator: '$gte', value });
        return this;
    }

    /**
     * In array condition
     */
    in(field: string, values: unknown[]): this {
        this.conditions.push({ field, operator: '$in', value: values });
        return this;
    }

    /**
     * Not in array condition
     */
    notIn(field: string, values: unknown[]): this {
        this.conditions.push({ field, operator: '$notIn', value: values });
        return this;
    }

    /**
     * Contains string condition (case-insensitive substring match)
     */
    contains(field: string, value: string): this {
        this.conditions.push({ field, operator: '$contains', value });
        return this;
    }

    /**
     * Field is not null condition
     */
    notNull(field: string): this {
        this.conditions.push({ field, operator: '$notNull', value: true });
        return this;
    }

    /**
     * Field is null condition
     */
    isNull(field: string): this {
        this.conditions.push({ field, operator: '$null', value: true });
        return this;
    }

    /**
     * Custom condition (advanced)
     */
    custom(field: string, operator: FilterOperator, value?: unknown): this {
        this.conditions.push({ field, operator, value });
        return this;
    }

    /**
     * Build the query string
     * @returns Query string like "filters[field1][$op]=value&filters[field2][$op]=value"
     */
    build(): string {
        if (this.conditions.length === 0) {
            return '';
        }

        const parts = this.conditions.map(cond => {
            const encodedValue = this.encodeValue(cond.value);
            return `filters[${cond.field}][${cond.operator}]=${encodedValue}`;
        });

        return parts.join('&');
    }

    /**
     * Encode value for URL query string
     */
    private encodeValue(value: unknown): string {
        if (value === null) {
            return 'true';
        }

        if (typeof value === 'boolean') {
            return value ? 'true' : 'false';
        }

        if (Array.isArray(value)) {
            return value.map(v => encodeURIComponent(String(v))).join(',');
        }

        if (typeof value === 'object') {
            return encodeURIComponent(JSON.stringify(value));
        }

        // At this point, value is string or number
        // eslint-disable-next-line @typescript-eslint/no-base-to-string
        return encodeURIComponent(String(value));
    }

    /**
     * Clear all conditions
     */
    clear(): this {
        this.conditions = [];
        return this;
    }

    /**
     * Clone the builder
     */
    clone(): StrapiFilterBuilder {
        const cloned = new StrapiFilterBuilder();
        cloned.conditions = [...this.conditions];
        return cloned;
    }
}

/**
 * Create a new filter builder
 */
export function filter(): StrapiFilterBuilder {
    return new StrapiFilterBuilder();
}

/**
 * Preset: Parking admin group filter
 * @param parkingId - The parking ID to filter by
 */
export function adminGroupFilterByParking(parkingId: string | number): string {
    return filter()
        .equals('relatesTo.targetId', parkingId)
        .build();
}

/**
 * Preset: Reservation classes by parking
 * @param parkingId - The parking ID to filter by
 */
export function resaClassesByParking(parkingId?: number | string): string {
    if (!parkingId) {
        return filter().notNull('parkingId').build();
    }
    return filter().equals('parkingId', parkingId).build();
}

/**
 * Preset: Groups by prefix
 * @param prefix - The prefix to filter by
 */
export function groupsByPrefix(prefix: string): string {
    return filter().equals('prefix', prefix).build();
}

/**
 * Preset: Groups by IDs
 * @param ids - Array of group IDs to filter by
 */
export function groupsByIds(ids: number[]): string {
    return filter().in('id', ids).build();
}

/**
 * Preset: Filter by multiple IDs
 * @param field - The field name
 * @param ids - Array of IDs to filter by
 */
export function byIds(field: string, ids: number[] | string[]): string {
    return filter().in(field, ids).build();
}

/**
 * Preset: Filter by ID
 * @param id - The ID value
 */
export function byId(id: number | string): string {
    return filter().equals('id', id).build();
}
